import { useCallback } from 'react';
import isNil from 'lodash/isNil';

import {
  UniversalFilterNumberModes,
  UniversalFilterNumberFilterState,
  UniversalFilterNumberOnChange,
  UniversalFilterNumberValue
} from '../../UniversalFilterNumber.types';

interface UniversalFilterNumberFilterStateOptions {
  onChange: UniversalFilterNumberOnChange;
}

function useUniversalFilterNumberFilterState({
  onChange
}: UniversalFilterNumberFilterStateOptions) {
  // All modes and options available in handleApplyFilterState
  // must be implemented in handleParseFilterValue too
  const handleApplyFilterState = useCallback<
    (filterState: UniversalFilterNumberFilterState) => void
  >(
    (filterState) => {
      if (filterState?.mode === UniversalFilterNumberModes.IS_NOT_EMPTY) {
        onChange({ isNotNull: true });
        return;
      }

      if (filterState?.mode === UniversalFilterNumberModes.IS_EMPTY) {
        onChange({ isNull: true });
        return;
      }

      const value = filterState?.value;
      const notEmptyValue = value !== undefined && value !== null;

      if (filterState?.mode === UniversalFilterNumberModes.IS) {
        onChange(notEmptyValue ? { eq: value } : null);
        return;
      }

      if (filterState?.mode === UniversalFilterNumberModes.IS_NOT) {
        onChange(notEmptyValue ? { notEq: value } : null);
        return;
      }

      if (filterState?.mode === UniversalFilterNumberModes.GREATER_THAN_EQUAL) {
        onChange(notEmptyValue ? { gte: value } : null);
        return;
      }

      if (filterState?.mode === UniversalFilterNumberModes.LESS_THAN_EQUAL) {
        onChange(notEmptyValue ? { lte: value } : null);
        return;
      }

      onChange();
    },
    [onChange]
  );

  const handleParseFilterValue = useCallback<
    (
      filterValue: UniversalFilterNumberValue
    ) => UniversalFilterNumberFilterState | undefined
  >((filterValue) => {
    if (isNil(filterValue)) {
      return undefined;
    }

    if (filterValue.isNotNull === true) {
      return {
        mode: UniversalFilterNumberModes.IS_NOT_EMPTY
      };
    }

    if (filterValue.isNull === true) {
      return {
        mode: UniversalFilterNumberModes.IS_EMPTY
      };
    }

    if (filterValue.eq) {
      return {
        mode: UniversalFilterNumberModes.IS,
        text: filterValue.eq
      };
    }

    if (filterValue.notEq) {
      return {
        mode: UniversalFilterNumberModes.IS_NOT,
        text: filterValue.notEq
      };
    }

    if (filterValue.gte) {
      return {
        mode: UniversalFilterNumberModes.GREATER_THAN_EQUAL,
        text: filterValue.gte
      };
    }

    if (filterValue.lte) {
      return {
        mode: UniversalFilterNumberModes.LESS_THAN_EQUAL,
        text: filterValue.lte
      };
    }

    return undefined;
  }, []);

  return { handleApplyFilterState, handleParseFilterValue };
}

export default useUniversalFilterNumberFilterState;
